import { useEffect, useMemo } from "react"
import { UseFormReturn, useForm } from "react-hook-form"

import { useAssignRef } from "shared/lib/useAssignRef"

import { zodResolver } from "@hookform/resolvers/zod"
import {
  RoomFormData,
  RoomFormDataSchema,
  RoomFormState
} from "features/room-management/types/RoomFormData"
import { RoomFormOptions } from "features/room-management/types/RoomFormOptions"
import { RoomFormTabType } from "features/room-management/types/RoomFormTabType"

import { RoomFormProps } from "../RoomForm"
import { RoomFormAttachmentsContextType } from "../context/RoomFormAttachmentsContext"

export interface RoomFormViewModel extends RoomFormOptions {
  form: UseFormReturn<RoomFormState, unknown, RoomFormData>
  onSubmit: () => void
  defaultTab?: RoomFormTabType
  attachmentsContext: RoomFormAttachmentsContextType
}

export function useRoomFormViewModel({
  defaultFormData,
  onSubmit,
  onChange,
  formRef,
  isReadonly,
  attachments,
  linkedAttachments,
  attachmentCategories,
  onUpload,
  ...rest
}: RoomFormProps): RoomFormViewModel {
  const defaultAttachmentLinks = useMemo(() => {
    return Object.fromEntries(
      linkedAttachments?.map(attachment => [attachment.id, true]) ?? []
    )
  }, [linkedAttachments])
  const form = useForm<RoomFormState, unknown, RoomFormData>({
    defaultValues: { ...defaultFormData, attachments: defaultAttachmentLinks },
    resolver: zodResolver<RoomFormState, unknown, RoomFormData>(
      RoomFormDataSchema
    )
  })

  useAssignRef({ ref: formRef, value: form })

  useEffect(() => {
    if (!onChange) return undefined
    return form.subscribe({
      callback: ({ values }) => {
        onChange?.(values)
      },
      formState: { values: true }
    })
  }, [form, onChange])

  useEffect(() => {
    if (isReadonly) {
      form.reset({ ...defaultFormData, attachments: defaultAttachmentLinks })
    }
  }, [isReadonly])

  const handleSubmit = useMemo(() => {
    return form.handleSubmit(formData => onSubmit?.(formData))
  }, [form, onSubmit])

  useEffect(() => {
    form.reset(
      { ...defaultFormData, attachments: defaultAttachmentLinks },
      { keepDirty: true, keepDirtyValues: true }
    )
  }, [defaultFormData, defaultAttachmentLinks])

  const attachmentsContext = useMemo<RoomFormAttachmentsContextType>(
    () => ({
      attachments: attachments ?? null,
      attachmentCategories: attachmentCategories ?? null,
      linkedAttachments: linkedAttachments ?? null,
      onUpload
    }),
    [attachments, attachmentCategories, linkedAttachments, onUpload]
  )

  return {
    form,
    onSubmit: handleSubmit,
    attachmentsContext,
    ...rest
  }
}
